import { bool, int, obj, str, type Json } from "./json";

/**
 * 网关推送的一条事件（JSON-RPC 通知 method == "event"）。
 * 保持“通用外壳 + 按需读取”：新版 Hermes 增加事件类型时，客户端只是多收到一个不认识的 type，不会出错。
 */
export interface GatewayEvent {
  type: string;
  /** 实时会话 id（session.create / resume 返回的 session_id）。全局事件为空。 */
  sessionID?: string;
  /** 会话内单调递增序号，用于断线重放去重。 */
  seq?: number;
  payload: Json;
  /** 是否来自 session.events.since 重放 */
  replayed: boolean;
}

export function parseEvent(params: unknown, replayed = false): GatewayEvent | undefined {
  const p = obj(params);
  const type = str(p?.type);
  if (!p || !type) return undefined;
  const sid = str(p.session_id);
  return {
    type,
    sessionID: sid ? sid : undefined,
    seq: int(p.seq),
    payload: (p.payload ?? null) as Json,
    replayed: replayed || bool(p.replayed) === true,
  };
}

export const EventType = {
  gatewayReady: "gateway.ready",
  messageStart: "message.start",
  messageDelta: "message.delta",
  messageInterim: "message.interim",
  messageComplete: "message.complete",
  reasoningDelta: "reasoning.delta",
  reasoningAvailable: "reasoning.available",
  thinkingDelta: "thinking.delta",
  toolGenerating: "tool.generating",
  toolStart: "tool.start",
  toolComplete: "tool.complete",
  toolOutputRisk: "tool.output_risk",
  statusUpdate: "status.update",
  todoUpdated: "todo.updated",
  sessionInfo: "session.info",
  sessionTitle: "session.title",
  sessionUsage: "session.usage",
  sessionReclaimed: "session.reclaimed",
  sessionsChanged: "sessions.changed",
  cronChanged: "cron.changed",
  error: "error",
  notice: "notice",
  notificationShow: "notification.show",
  requestCancel: "request.cancel",
  backgroundComplete: "background.complete",
  btwComplete: "btw.complete",
  reviewSummary: "review.summary",
  subagentSpawnRequested: "subagent.spawn_requested",
  subagentStart: "subagent.start",
  subagentProgress: "subagent.progress",
  subagentTool: "subagent.tool",
  subagentComplete: "subagent.complete",
  /** 客户端合成事件：该实时会话无法通过重放补齐，需要用 session.resume 重建 */
  clientResync: "timibot.resync",
} as const;

export const ServerRequestMethod = {
  approval: "approval",
  clarify: "clarify",
  sudo: "sudo",
  secret: "secret",
  /**
   * 能回答的请求。其余是桌面专属，收到时立即回 -32601。
   * display.install.sudo：安装 Bot 电脑的桌面组件时要 sudo 密码（iOS 的列表里漏了它，请求会被直接拒绝）。
   */
  supportedOnMobile: ["approval", "clarify", "sudo", "secret", "display.install.sudo"] as string[],
};

/** 服务端→客户端请求（阻塞式交互：批准、澄清、sudo…） */
export interface ServerRequest {
  id: string;
  method: string;
  params: Json;
  /** 断线重连后通过 open_requests 补发的请求 */
  replayed: boolean;
}

export function requestSessionID(r: ServerRequest): string | undefined {
  return str(obj(r.params)?.session_id);
}

export type ServerRequestReply = { result: Json } | { error: { code: number; message: string } };

export const methodNotFound: ServerRequestReply = { error: { code: -32601, message: "no handler for server request" } };
